/* eslint-disable @typescript-eslint/no-explicit-any */
import Peer from '@bowbee/peer-lite';
import type { PlaycastWebrtcPreset, PlaycastWebrtcEncoder, RTCRtpCodecCapability, PlaycastWebrtcPresetGroup } from './typesMedia';
import { debug } from './util';
import { defaultQualityPresetStore, h264Config } from './defaultConfigs';
import type { PlaycastPeerClientMetadata, PlaycastChannels, StateStore } from '@playcastdotio/CoreTypes';

const allAvailableEncodings: PlaycastWebrtcEncoder[] = ['h264','vp8', 'vp9', 'av1'];
const globalOrder: PlaycastWebrtcEncoder[] = ['h264','av1', 'vp9', 'vp8'];

let availableVideoEncodingTypes: PlaycastWebrtcEncoder[] = [];
const sdpFmtpLines: { [key in PlaycastWebrtcEncoder]: string[] } = {
  vp8: [],
  vp9: [],
  h264: [],
  av1: [],
};

export const getAvailableVideoEncodingTypes = (): PlaycastWebrtcEncoder[] => {
  if (availableVideoEncodingTypes.length !== 0) {
    return availableVideoEncodingTypes;
  }
  const capabilities = RTCRtpSender.getCapabilities('video');
  if (!capabilities || !capabilities.codecs) {
    throw new Error('Failed to get video codec capabilities');
  }
  const supportedCodecs: PlaycastWebrtcEncoder[] = [];
  capabilities.codecs.forEach((codec) => {
    const encodingType = codec.mimeType.split('/')[1].toLowerCase() as PlaycastWebrtcEncoder;
    if (allAvailableEncodings.includes(encodingType)) {
      supportedCodecs.push(encodingType);
    }
  });
  availableVideoEncodingTypes = supportedCodecs;
  return supportedCodecs;
};

let receiverCapabilities: RTCRtpCodecCapability[] = [];
export const getReceiverCapabilities = (): RTCRtpCodecCapability[] => {
  if (receiverCapabilities.length !== 0) {
    return receiverCapabilities;
  }
  const capabilities = RTCRtpReceiver.getCapabilities('video');
  if (!capabilities || !capabilities.codecs) {
    throw new Error('Failed to get video codec capabilities');
  }
  receiverCapabilities = capabilities.codecs;
  receiverCapabilities.forEach((codec) => {
    if (codec.mimeType === 'video/VP8') {
      sdpFmtpLines.vp8.push(codec.sdpFmtpLine || '');
    } else if (codec.mimeType === 'video/VP9') {
      sdpFmtpLines.vp9.push(codec.sdpFmtpLine || '');
    } else if (codec.mimeType === 'video/H264') {
      sdpFmtpLines.h264.push(codec.sdpFmtpLine || '');
    } else if (codec.mimeType === 'video/AV1') {
      sdpFmtpLines.av1.push(codec.sdpFmtpLine || '');
    }
  });
  return receiverCapabilities;
};

const findBestH264SdpLinesForIos = (): RTCRtpCodecCapability[] => {
  const h264Lines = sdpFmtpLines.h264;
  const capabilities: RTCRtpCodecCapability[] = [];

  // Helper to check profile and level
  const hasProfileAndLevel = (line: string, profile: string, minLevel: string) => {
    const match = line.match(/profile-level-id=([0-9a-fA-F]{6})/i);
    if (!match) return false;
    const profileId = match[1].toLowerCase();
    return profileId.startsWith(profile) && profileId.substring(4) >= minLevel;
  };

  // Helper to create capability
  const createCapability = (line: string): RTCRtpCodecCapability => ({
    mimeType: 'video/H264',
    clockRate: 90000,
    sdpFmtpLine: line,
  });

  // Try Baseline with at least level 3.1 first (most compatible)
  const baseline = h264Lines.find((line) => hasProfileAndLevel(line, '4200', '1f'));
  if (baseline) capabilities.push(createCapability(baseline));

  // Then try Constrained Baseline with at least level 3.1
  const constrainedBaseline = h264Lines.find((line) => hasProfileAndLevel(line, '42e0', '1f'));
  if (constrainedBaseline) capabilities.push(createCapability(constrainedBaseline));

  // Then try Main Profile with at least level 3.1
  const mainProfile = h264Lines.find((line) => hasProfileAndLevel(line, '4d00', '1f'));
  if (mainProfile) capabilities.push(createCapability(mainProfile));

  // Add any remaining lines as fallbacks
  h264Lines.forEach((line) => {
    if (!capabilities.some((cap) => cap.sdpFmtpLine === line)) {
      capabilities.push(createCapability(line));
    }
  });

  return capabilities;
};

const findBestH264SdpLines = (isApple?: boolean, isFirefox?: boolean): RTCRtpCodecCapability[] => {
  console.log('[Presets] Finding best H264 SDP lines', isApple, isFirefox);
  const h264Lines = sdpFmtpLines.h264;
  if (!h264Lines.length) return [];

  // Helper to check hardware encoding parameters
  const isHardwareCapable = (line: string): boolean => {
    return line.includes('packetization-mode=1') && line.includes('level-asymmetry-allowed=1');
  };

  // Get profile ID from line
  const getProfileId = (line: string): string | null => {
    const match = line.match(/profile-level-id=([0-9a-fA-F]{6})/i);
    return match ? match[1].toLowerCase() : null;
  };

  // Known profiles that might work for both hardware encoding and iOS/Firefox
  const potentialProfiles = [
    '4d001f', // Main Profile Level 3.1 (4D001F)
    '42c01f', // Constrained Baseline Level 3.1 (alternative to 42e01f)
    '42c028', // Constrained Baseline Level 4.0
    '42401f', // Baseline Level 3.1 (alternative)
    '424028', // Baseline Level 4.0
    '4d401f', // Main Level 3.1
    '4d4028', // Main Level 4.0
  ];

  const capabilities: RTCRtpCodecCapability[] = [];

  // Helper to create capability
  const createCapability = (line: string): RTCRtpCodecCapability => ({
    mimeType: 'video/H264',
    clockRate: 90000,
    sdpFmtpLine: line,
  });

  if (isFirefox) {
    // Firefox works best with software encoding for H264
    const softwareLines = h264Lines.filter((line) => !isHardwareCapable(line));
    if (softwareLines.length > 0) {
      console.warn('[H264] Using software encoding for Firefox:', softwareLines);
      return softwareLines.map(createCapability);
    }
  }

  // Try to find hardware-capable lines with known-good profiles
  for (const profile of potentialProfiles) {
    const compatibleLine = h264Lines.find((line) => {
      const currentProfile = getProfileId(line);
      return isHardwareCapable(line) && currentProfile === profile;
    });

    if (compatibleLine) {
      console.debug(`[H264] Found compatible profile with hardware encoding: ${profile}`);
      capabilities.push(createCapability(compatibleLine));
    }
  }

  // If no hardware-capable lines found, try software encoding
  if (capabilities.length === 0) {
    const softwareLines = h264Lines.filter((line) => !isHardwareCapable(line));
    if (softwareLines.length > 0) {
      console.warn('[H264] Falling back to software encoding');
      return softwareLines.map(createCapability);
    }
  }

  // Log available profiles for debugging
  console.debug('[H264] Available profiles:');
  h264Lines.forEach((line) => {
    const profile = getProfileId(line);
    const hasHardware = isHardwareCapable(line);
    console.debug(`Profile: ${profile}, Hardware Capable: ${hasHardware}`);
  });

  // If still no capabilities, add all lines as fallbacks
  if (capabilities.length === 0) {
    console.warn('[H264] No optimal profiles found, using all available lines');
    return h264Lines.map(createCapability);
  }

  return capabilities;
};

const findBestH264SdpLinesForFirefox = (): RTCRtpCodecCapability[] => {
  const h264Lines = sdpFmtpLines.h264;
  const capabilities: RTCRtpCodecCapability[] = [];

  // Helper to check hardware encoding parameters
  const isHardwareCapable = (line: string): boolean => {
    return line.includes('packetization-mode=1') && line.includes('level-asymmetry-allowed=1');
  };

  // Helper to check profile and level
  const hasProfileAndLevel = (line: string, profile: string, minLevel: string) => {
    const match = line.match(/profile-level-id=([0-9a-fA-F]{6})/i);
    if (!match) return false;
    const profileId = match[1].toLowerCase();
    return profileId.startsWith(profile) && profileId.substring(4) >= minLevel;
  };

  // Helper to create capability
  const createCapability = (line: string): RTCRtpCodecCapability => ({
    mimeType: 'video/H264',
    clockRate: 90000,
    sdpFmtpLine: line,
  });

  // Firefox works best with software encoding for H264
  // First try to find software-encoded lines with known-good profiles
  const softwareLines = h264Lines.filter((line) => !isHardwareCapable(line));

  // Try Baseline profile first (most compatible)
  const baseline = softwareLines.find((line) => hasProfileAndLevel(line, '4200', '1f'));
  if (baseline) capabilities.push(createCapability(baseline));

  // Then try Constrained Baseline
  const constrainedBaseline = softwareLines.find((line) => hasProfileAndLevel(line, '42e0', '1f'));
  if (constrainedBaseline) capabilities.push(createCapability(constrainedBaseline));

  // Then try Main Profile
  const mainProfile = softwareLines.find((line) => hasProfileAndLevel(line, '4d00', '1f'));
  if (mainProfile) capabilities.push(createCapability(mainProfile));

  // Add any remaining software-encoded lines as fallbacks
  softwareLines.forEach((line) => {
    if (!capabilities.some((cap) => cap.sdpFmtpLine === line)) {
      capabilities.push(createCapability(line));
    }
  });

  // If we found no software-encoded lines, log a warning
  if (capabilities.length === 0) {
    console.warn('[H264] No software-encoded lines found for Firefox, falling back to hardware encoding');
    // Fall back to hardware encoding as last resort
    const hardwareLines = h264Lines.filter((line) => isHardwareCapable(line));
    if (hardwareLines.length > 0) {
      return hardwareLines.map(createCapability);
    }
  }

  return capabilities;
};

export const convertPlaycastEncodingTypeToSdpLines = (
  encodingTypes: PlaycastWebrtcEncoder | PlaycastWebrtcEncoder[],
  peerMetadata: PlaycastPeerClientMetadata,
  _lineOverride: RTCRtpCodecCapability | undefined,
): RTCRtpCodecCapability[] | undefined => {
  // Normalize to array
  const desiredEncoders: PlaycastWebrtcEncoder[] = Array.isArray(encodingTypes) ? encodingTypes : [encodingTypes];

  // For Firefox, avoid setCodecPreferences entirely to maximize compatibility
  if (peerMetadata?.isFirefoxBrowser) {
    return undefined;
  }

  // Use browser-reported capabilities and preserve their intrinsic order
  const availableSenders = getAvailableVideoEncodingTypes();
  const receiverCodecs = getReceiverCapabilities();

  // Build a set of desired mime subtypes (e.g., 'h264', 'vp9') that we can actually send
  const desiredSet = new Set(desiredEncoders.filter((e) => availableSenders.includes(e)).map((e) => e.toLowerCase()));

  // Select all receiver codecs whose subtype is in our desired set; keep per-codec intrinsic order
  const groupedBySubtype: Record<string, RTCRtpCodecCapability[]> = {};
  receiverCodecs.forEach((codec) => {
    const subtype = codec.mimeType.split('/')[1]?.toLowerCase();
    if (!subtype) return;
    if (!desiredSet.has(subtype as PlaycastWebrtcEncoder)) return;
    if (!groupedBySubtype[subtype]) groupedBySubtype[subtype] = [];
    groupedBySubtype[subtype].push(codec);
  });

  // Apply overall codec preference across groups without reordering lines inside a codec group
  const orderedTypes = globalOrder.filter((t) => desiredSet.has(t));
  const selected: RTCRtpCodecCapability[] = [];
  orderedTypes.forEach((t) => {
    const group = groupedBySubtype[t];
    if (group && group.length) selected.push(...group);
  });

  // Fallback: if nothing matched, return undefined to let browser negotiate defaults
  if (selected.length === 0) {
    return undefined;
  }

  return selected;
};

export const transformSdp = (sdp: RTCSessionDescriptionInit, preset: PlaycastWebrtcPresetGroup['default']) => {
  if (!sdp.sdp) return sdp;

  // Split into sections
  const sections = sdp.sdp.split('\r\nm=');

  // Process each section
  const processedSections = sections.map((section, index) => {
    // Add back the 'm=' prefix that was removed by split
    const fullSection = index === 0 ? section : `m=${section}`;

    // Skip non-video sections
    if (!fullSection.startsWith('m=video')) {
      return fullSection;
    }

    // Split into lines
    const lines = fullSection.split('\r\n');

    // Find the rtpmap line for H264
    let h264PayloadType: string | null = null;
    for (const line of lines) {
      if (line.startsWith('a=rtpmap:') && line.includes('H264/90000')) {
        h264PayloadType = line.split(':')[1].split(' ')[0];
        break;
      }
    }

    // If no H264 found, return section unchanged
    if (!h264PayloadType) {
      return fullSection;
    }

    // Process fmtp lines
    const processedLines = lines.map((line) => {
      if (line.startsWith(`a=fmtp:${h264PayloadType}`)) {
        // Get existing parameters
        const existingParams = line.split(' ')[1] || '';

        // Remove any existing parameters we're about to add
        let cleanParams = existingParams
          .replace(/sps-pps-idr-in-keyframe=1;?/, '')
          .replace(/x-google-max-bitrate=\d+;?/, '')
          .replace(/x-google-min-bitrate=\d+;?/, '')
          .replace(/x-google-start-bitrate=\d+;?/, '')
          .replace(/;$/, ''); // Remove trailing semicolon if present

        // Add new parameters if configured
        const newParams: string[] = [];
        if (h264Config?.enableSpsPpsIdrInKeyframe) {
          newParams.push('sps-pps-idr-in-keyframe=1');
        }
        if (h264Config?.enableMaxBitrate && h264Config.maxBitrate) {
          newParams.push(`x-google-max-bitrate=${h264Config.maxBitrate}`);
        }
        if (h264Config?.enableMinBitrate && h264Config.minBitrate) {
          newParams.push(`x-google-min-bitrate=${h264Config.minBitrate}`);
        }
        if (h264Config?.enableStartBitrate && h264Config.startBitrate) {
          newParams.push(`x-google-start-bitrate=${h264Config.startBitrate}`);
        }

        if (newParams.length > 0) {
          cleanParams += (cleanParams ? ';' : '') + newParams.join(';');
          return `a=fmtp:${h264PayloadType} ${cleanParams}`;
        }
      }
      return line;
    });

    return processedLines.join('\r\n');
  });

  // Reconstruct SDP
  let rebuilt = processedSections.join('\r\n');

  // Inject TIAS/AS bandwidth constraints for video m= sections if bitrate configured
  if (preset?.videoSend?.bitrate && preset.videoSend.bitrate > 0) {
    const lines = rebuilt.split('\r\n');
    const result: string[] = [];
    let insideVideo = false;
    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];
      if (line.startsWith('m=')) {
        // entering a new media section
        insideVideo = line.startsWith('m=video');
        result.push(line);
        continue;
      }
      if (insideVideo && line.startsWith('c=')) {
        // Immediately after c= in a video m-section, add b= lines if not already present
        result.push(line);
        const next1 = lines[i + 1] || '';
        const next2 = lines[i + 2] || '';
        const haveB = next1.startsWith('b=') || next2.startsWith('b=');
        if (!haveB) {
          const kbps = Math.max(1, Math.floor(preset.videoSend.bitrate / 1000));
          result.push(`b=TIAS:${preset.videoSend.bitrate}`);
          result.push(`b=AS:${kbps}`);
        }
        continue;
      }
      result.push(line);
    }
    rebuilt = result.join('\r\n');
  }

  // Handle feedback types
  sdp.sdp = rebuilt;

  if (!preset.videoSend?.disableFeedbackTypes) return sdp;
  const feedbackTypes = preset.videoSend.disableFeedbackTypes;

  if (feedbackTypes.googRemb) {
    sdp.sdp = sdp.sdp.replace(/a=rtcp-fb:\d+ goog-remb\r\n/g, '');
  }
  if (feedbackTypes.transportCc) {
    sdp.sdp = sdp.sdp.replace(/a=rtcp-fb:\d+ transport-cc\r\n/g, '');
  }
  if (feedbackTypes.ccmFir) {
    sdp.sdp = sdp.sdp.replace(/a=rtcp-fb:\d+ ccm fir\r\n/g, '');
  }
  if (feedbackTypes.nack) {
    sdp.sdp = sdp.sdp.replace(/a=rtcp-fb:\d+ nack\r\n/g, '');
  }
  if (feedbackTypes.nackPli) {
    sdp.sdp = sdp.sdp.replace(/a=rtcp-fb:\d+ nack pli\r\n/g, '');
  }
  return sdp;
};

/** Widest value the WebRTC receiver hints accept, ms. */
const RECEIVER_HINT_MAX_MS = 4000;

/**
 * Apply the low-latency receiver hints (`jitterBufferTarget` + Chrome's `playoutDelayHint`)
 * for one video receiver.
 *
 * Must be called with the receiver's REAL track in place: the hints are re-derived by the
 * engine when media starts flowing, so a value set during pre-negotiation does not
 * necessarily survive to the first decoded frame.
 *
 * `0` is the configured production value and is the whole point of these hints — "drain the
 * buffer as fast as playback allows". It is also falsy, and a truthiness guard here silently
 * turns the low-latency path off while leaving the config reading as though it were on. That
 * is exactly what happened: Android fell back to Chrome's adaptive jitter buffer, which grew
 * to a standing 12–18 frame queue (0.5–0.9s of latency) on a 6ms-RTT LAN.
 */
export const applyLowLatencyReceiverHints = (receiver: RTCRtpReceiver, targetMs: number): void => {
  if (!Number.isFinite(targetMs) || targetMs < 0 || targetMs > RECEIVER_HINT_MAX_MS) {
    console.warn(`[Peer] Invalid jitterBufferTarget value: ${targetMs}ms. Must be between 0-${RECEIVER_HINT_MAX_MS}ms.`);
    return;
  }
  try {
    (receiver as unknown as { jitterBufferTarget: number }).jitterBufferTarget = targetMs;
    if ('playoutDelayHint' in (receiver as unknown as Record<string, unknown>)) {
      (receiver as unknown as { playoutDelayHint: number }).playoutDelayHint = targetMs / 1000;
    }
  } catch (err) {
    console.warn('[Peer] Failed to set low-latency receiver hints:', err);
  }
};

export const applyPresetToTransceivers = (
  peerId: string,
  peer: Peer,
  trackId: string,
  preset: Partial<PlaycastWebrtcPreset>,
  peerMetadata: PlaycastPeerClientMetadata,
  overrideDegradationPreference?: string,
) => {
  const senders = peer.get().getSenders();
  if (!senders) {
    debug('No sender found');
    // log all sender tracks for debug
    return;
  }
  senders.forEach((sender) => {
    // Clean up this sender if its not doing anything
    if (!sender.track) {
      peer.get().removeTrack(sender);
      return;
    }

    const params: any = sender.getParameters();
    if (!params.encodings) {
      params.encodings = [{}] as RTCRtpEncodingParameters[];
    }

    if (sender.track?.kind === 'audio') {
      // Audio tracks don't have encodings
      return;
    }

    // Apply the preset values to the encoding parameters
    params.encodings = params.encodings.map((encoding: RTCRtpEncodingParameters) => ({
      ...encoding,
      maxBitrate: preset.videoSend?.bitrate ?? encoding.maxBitrate,
      maxFramerate: preset.videoSend?.framerate ?? encoding.maxFramerate,
      scaleResolutionDownBy: 1,
      networkPriority: 'high' as RTCPriorityType,
      priority: 'high' as RTCPriorityType,
    }));
    params.degradationPreference = overrideDegradationPreference ?? preset.videoSend?.degradationPreference ?? 'maintain-framerate';

    debug('Applying encoding parameters', params.encodings);
    // only set the params if getParameters() has been called on this sender
    try {
      (sender as RTCRtpSender).setParameters(params);
    } catch (err) {
      debug('Failed to set parameters', err);
    }
  });

  if (preset.videoSend?.keyframeInterval) {
    manageKeyframeInterval.start(peerId, trackId, peer, preset.videoSend.keyframeInterval);
  } else {
    manageKeyframeInterval.stop(peerId, trackId);
  }

  const transcievers = peer.get().getTransceivers();
  transcievers.forEach((transciever) => {
    const isVideo =
      (transciever.sender?.track && transciever.sender.track.kind === 'video') ||
      (transciever.receiver?.track && transciever.receiver.track.kind === 'video');
    if (isVideo) {
      if (preset.videoReceive?.jitterBufferTarget !== undefined) {
        applyLowLatencyReceiverHints(transciever.receiver, preset.videoReceive.jitterBufferTarget);
      }

      // Build sender-based codec preferences with global priority: h264 > av1 > vp9 > vp8
      // Skip on Firefox to avoid negotiation incompatibilities
      if (peerMetadata?.isFirefoxBrowser) {
        return;
      }
      try {
        const preferredList = Array.isArray(preset.videoReceive?.encoder)
          ? (preset.videoReceive?.encoder as PlaycastWebrtcEncoder[])
          : preset.videoReceive?.encoder
            ? [preset.videoReceive?.encoder]
            : undefined;
        const senderCaps = RTCRtpSender.getCapabilities('video');
        if (senderCaps?.codecs && senderCaps.codecs.length > 0) {
          const groupBySubtype: Record<string, RTCRtpCodecCapability[]> = {} as any;
          senderCaps.codecs.forEach((c) => {
            const subtype = c.mimeType.split('/')[1]?.toLowerCase();
            if (!subtype) return;
            if (!groupBySubtype[subtype]) groupBySubtype[subtype] = [];
            groupBySubtype[subtype].push(c as any);
          });
          const prefLower = preferredList ? preferredList.map((e) => e.toLowerCase() as PlaycastWebrtcEncoder) : [];
          const baseOrder: PlaycastWebrtcEncoder[] =
            prefLower.length > 0
              ? [
                  ...prefLower.filter((e) => (globalOrder as PlaycastWebrtcEncoder[]).includes(e)),
                  ...globalOrder.filter((x) => !prefLower.includes(x)),
                ]
              : [...globalOrder];
          const codecPrefs: RTCRtpCodecCapability[] = [];
          baseOrder.forEach((t) => {
            const group = groupBySubtype[t];
            if (group && group.length) codecPrefs.push(...group);
          });
          if (codecPrefs.length > 0) {
            transciever.setCodecPreferences(codecPrefs);
          }
        }
      } catch (e) {
        console.warn('[Peer] Failed to set sender-based codec preferences:', e);
      }
    }
  });
};

// export const applyPresetToReceivers = (peer: Peer, presets: PlaycastWebrtcPresetGroup, trackData: {[trackId: string]: PlaycastPeerMediaMetadata}) => {
//   // Iterate tracks, see if they match trackdata. If they do, apply the preset. If they are unknown, apply default.
//   const transcievers = peer.get().getTransceivers();
//   transcievers.forEach((transciever) => {
//     const receiver = transciever.receiver;
//     if(!receiver) return;
//     if(!receiver.track) return;
//     if(receiver.track.kind !== 'video') return;

//     const trackId = receiver.track.id;
//     let metadata = trackData[trackId];
//     if (!metadata) {
//       metadata = { channel: 'default', type: 'video' };
//     }
//     if (metadata.type === 'video') {
//       if (preset.videoReceive?.encoder) {
//         const codecPreferences = convertPlaycastEncodingTypeToSdpLines(preset.videoReceive.encoder);
//         transciever.setCodecPreferences(codecPreferences);
//       }
//     }
//   });
// };

export const applyCodecPreferencesToPeer = (
  peer: Peer,
  presetGroup: PlaycastWebrtcPresetGroup,
  metadata: PlaycastPeerClientMetadata,
  channelId?: PlaycastChannels,
) => {
  const transceivers = peer.get().getTransceivers();

  transceivers.forEach((transceiver) => {
    // Skip if not a video transceiver
    if (!transceiver.receiver.track || transceiver.receiver.track.kind !== 'video') {
      return;
    }

    // Determine preferred encoders: use channel preset if provided; otherwise allow browser-global order
    let preferredEncoders: PlaycastWebrtcEncoder[] | undefined;
    if (channelId && presetGroup[channelId]?.videoReceive?.encoder) {
      const enc = presetGroup[channelId]?.videoReceive?.encoder as PlaycastWebrtcEncoder | PlaycastWebrtcEncoder[];
      preferredEncoders = Array.isArray(enc) ? enc : [enc];
    } else {
      preferredEncoders = undefined;
    }

    // Low-latency receiver hints
    const jitter =
      (channelId ? presetGroup[channelId]?.videoReceive?.jitterBufferTarget : undefined) ??
      defaultQualityPresetStore.currentState.default.videoReceive?.jitterBufferTarget;
    if (jitter !== undefined) {
      applyLowLatencyReceiverHints(transceiver.receiver, jitter);
    }

    // Sender-based codec preferences with priority h264 > av1 > vp9 > vp8
    // Skip on Firefox to avoid incompatibilities
    if (metadata?.isFirefoxBrowser) {
      return;
    }
    try {
      const senderCaps = RTCRtpSender.getCapabilities('video');
      if (senderCaps?.codecs && senderCaps.codecs.length > 0) {
        const groupBySubtype: Record<string, RTCRtpCodecCapability[]> = {} as any;
        senderCaps.codecs.forEach((c) => {
          const subtype = c.mimeType.split('/')[1]?.toLowerCase();
          if (!subtype) return;
          if (!groupBySubtype[subtype]) groupBySubtype[subtype] = [];
          groupBySubtype[subtype].push(c as any);
        });
        const allowed = preferredEncoders && preferredEncoders.length > 0 ? new Set(preferredEncoders.map((e) => e.toLowerCase())) : null;
        const baseOrder: PlaycastWebrtcEncoder[] =
          allowed && preferredEncoders
            ? [...preferredEncoders.map((e) => e.toLowerCase() as PlaycastWebrtcEncoder), ...globalOrder.filter((g) => !allowed.has(g))]
            : [...globalOrder];
        const codecPrefs: RTCRtpCodecCapability[] = [];
        baseOrder.forEach((t) => {
          const group = groupBySubtype[t];
          if (group && group.length) codecPrefs.push(...group);
        });
        if (codecPrefs.length > 0) {
          transceiver.setCodecPreferences(codecPrefs);
        }
      }
    } catch (e) {
      console.warn('[Peer] Failed to set sender-based codec preferences:', e);
    }
  });
};

const keyframeIntervals: Record<string, NodeJS.Timeout> = {};
const getIntervalKey = (peerId: string, trackId: string) => `${peerId}:${trackId}`;

// Manage keyframe intervals
const ARE_KEYFRAMES_DISABLED = true;
export const manageKeyframeInterval = {
  start: (peerId: string, trackId: string, peer: Peer, intervalMs: number) => {
    if (ARE_KEYFRAMES_DISABLED) return;
    const key = getIntervalKey(peerId, trackId);

    // Clear existing interval if it exists
    manageKeyframeInterval.stop(peerId, trackId);

    // Create new interval
    const intervalId = setInterval(() => {
      const senders = peer.get().getSenders();
      senders.forEach((sender) => {
        if (sender?.track?.id === trackId && sender.track.kind === 'video') {
          // also ensure the peer is still connected
          if (peer.get().connectionState !== 'connected') {
            debug('Peer is not connected, stopping keyframe interval');
            manageKeyframeInterval.stop(peerId, trackId);
            return;
          }
          const param = sender.getParameters();
          try {
            debug('Requesting keyframe for', trackId, 'with interval', intervalMs);
            (sender as any).setParameters(param, {
              encodingOptions: [{ keyFrame: true }],
            });
          } catch (err) {
            debug('Failed to request keyframe', err);
          }
        }
      });
    }, intervalMs);

    // Store the interval
    keyframeIntervals[key] = intervalId;

    return intervalId;
  },

  stop: (peerId: string, trackId: string) => {
    const key = getIntervalKey(peerId, trackId);
    const existingInterval = keyframeIntervals[key];
    if (existingInterval) {
      clearInterval(existingInterval);
      delete keyframeIntervals[key];
    }
  },

  stopAllForPeer: (peerId: string) => {
    debug('Stopping all keyframe intervals for peer', peerId);
    // Find and clear all intervals for this peer
    const peerPrefix = `${peerId}:`;
    for (const key in keyframeIntervals) {
      if (key.startsWith(peerPrefix)) {
        clearInterval(keyframeIntervals[key]);
        delete keyframeIntervals[key];
      }
    }
  },

  // Utility function to check if an interval exists
  exists: (peerId: string, trackId: string) => {
    return keyframeIntervals[getIntervalKey(peerId, trackId)] !== undefined;
  },
};

// Helper to get encoder profile for a track
export const getEncoderProfile = (
  track: MediaStreamTrack,
  channelId: PlaycastChannels,
  qualityPresetStore: StateStore<PlaycastWebrtcPresetGroup>,
): PlaycastWebrtcEncoder | PlaycastWebrtcEncoder[] | undefined => {
  const preset = qualityPresetStore.currentState[channelId] ?? qualityPresetStore.currentState.default;
  return preset?.videoReceive?.encoder ?? 'vp8';
};

// Helper to check if encoder profiles match
export const doEncodersMatch = (
  track1: MediaStreamTrack,
  channel1: PlaycastChannels,
  track2: MediaStreamTrack,
  channel2: PlaycastChannels,
  qualityPresetStore: StateStore<PlaycastWebrtcPresetGroup>,
): boolean => {
  const profile1 = getEncoderProfile(track1, channel1, qualityPresetStore);
  const profile2 = getEncoderProfile(track2, channel2, qualityPresetStore);

  // Profiles can be arrays. If any match, return true
  if (Array.isArray(profile1) && Array.isArray(profile2)) {
    return profile1.some((p1) => profile2.includes(p1));
  }
  // Else, compare directly
  return profile1 === profile2;
};
